import { useState, useEffect } from 'react';
import { api } from '../api';
import { LEAVE_TYPES, STATUS_LABELS, formatDate } from '../constants';

export default function ReportsPage({ user }) {
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ userId: '', status: '', type: '', year: new Date().getFullYear().toString() });

  useEffect(() => {
    Promise.all([api.getLeaves(), api.getUsers()])
      .then(([l, u]) => { setLeaves(l); setUsers(u); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = leaves.filter(l => {
    if (filters.userId && l.user_id !== parseInt(filters.userId)) return false;
    if (filters.status && l.status !== filters.status) return false;
    if (filters.type && l.type !== filters.type) return false;
    if (filters.year && !l.date_from.startsWith(filters.year)) return false;
    return true;
  });

  const totalDays = filtered.filter(l => l.status === 'approved').reduce((s, l) => s + l.days_count, 0);

  const exportCSV = () => {
    const rows = [
      ['Pracownik', 'Email', 'Typ', 'Od', 'Do', 'Dni', 'Status', 'Data złożenia', 'Komentarz pracownika', 'Komentarz managera'],
      ...filtered.map(l => [
        l.user_name, l.user_email,
        LEAVE_TYPES[l.type]?.label || l.type,
        l.date_from, l.date_to, l.days_count,
        STATUS_LABELS[l.status]?.label || l.status,
        l.created_at?.slice(0, 10),
        l.note || '',
        l.manager_note || '',
      ])
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dartom_urlopy_${filters.year || 'wszystkie'}.csv`;
    a.click();
  };

  const userStats = users.map(u => {
    const userLeaves = leaves.filter(l => l.user_id === u.id && l.status === 'approved');
    const thisYear = userLeaves.filter(l => l.date_from.startsWith(new Date().getFullYear().toString()));
    return { ...u, totalDays: thisYear.reduce((s, l) => s + l.days_count, 0), requestsCount: thisYear.length };
  });

  if (loading) return <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Ładowanie...</div>;

  return (
    <div>
      {/* Summary stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-value">{filtered.length}</div>
          <div className="stat-label">Wniosków (filtr)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalDays}</div>
          <div className="stat-label">Zatwierdzonych dni</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{filtered.filter(l => l.status === 'pending').length}</div>
          <div className="stat-label">Oczekujących</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Pracowników</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">Filtry</span>
          <button className="btn btn-yellow btn-sm" onClick={exportCSV}>↓ Eksport CSV</button>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">Rok</label>
              <select className="form-input" value={filters.year} onChange={e => setFilters(p => ({ ...p, year: e.target.value }))}>
                <option value="">Wszystkie</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
            </div>
            <div>
              <label className="form-label">Pracownik</label>
              <select className="form-input" value={filters.userId} onChange={e => setFilters(p => ({ ...p, userId: e.target.value }))}>
                <option value="">Wszyscy</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-input" value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
                <option value="">Wszystkie</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Typ urlopu</label>
              <select className="form-input" value={filters.type} onChange={e => setFilters(p => ({ ...p, type: e.target.value }))}>
                <option value="">Wszystkie</option>
                {Object.entries(LEAVE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Employee summary */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><span className="card-title">Zestawienie pracowników ({new Date().getFullYear()})</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead><tr><th>Pracownik</th><th>Email</th><th>Rola</th><th>Wnioski (rok)</th><th>Dni zatwierdzonych</th></tr></thead>
            <tbody>
              {userStats.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td style={{ color: 'var(--text3)' }}>{u.email}</td>
                  <td><span className="badge" style={{ background: u.role === 'manager' ? '#eaf4e6' : '#f0f0f0', color: u.role === 'manager' ? '#2d6a1f' : '#666' }}>{u.role === 'manager' ? 'Manager' : 'Pracownik'}</span></td>
                  <td>{u.requestsCount}</td>
                  <td style={{ fontWeight: 700 }}>{u.totalDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leaves table */}
      <div className="card">
        <div className="card-header"><span className="card-title">Lista wniosków ({filtered.length})</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Pracownik</th><th>Typ</th><th>Od</th><th>Do</th><th>Dni</th><th>Status</th><th>Złożono</th><th>Komentarz</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Brak wyników</td></tr>
              ) : filtered.map(l => {
                const typeConf = LEAVE_TYPES[l.type] || LEAVE_TYPES.other;
                const statusConf = STATUS_LABELS[l.status] || STATUS_LABELS.pending;
                return (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 600 }}>{l.user_name}</td>
                    <td><span className="badge" style={{ background: typeConf.bg, color: typeConf.color }}>{typeConf.label}</span></td>
                    <td>{formatDate(l.date_from)}</td>
                    <td>{formatDate(l.date_to)}</td>
                    <td>{l.days_count}</td>
                    <td><span className="badge" style={{ background: statusConf.bg, color: statusConf.color }}>{statusConf.label}</span></td>
                    <td style={{ color: 'var(--text3)', fontSize: 13 }}>{l.created_at?.slice(0, 10)}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 200 }}>
                      {l.note || ''}{l.manager_note ? ` | Manager: ${l.manager_note}` : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
